import { ComponentRef } from '@angular/core';
import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from '@angular/router';

/**
 * #참조
 * https://stackoverflow.com/questions/41280471/how-to-implement-routereusestrategy-shoulddetach-for-specific-routes-in-angular 
 * https://johnny-mh.github.io/post/angular%EC%9D%98-routerreusestrategy
 * https://velog.io/@noyo0123/%EC%95%B5%EA%B7%A4%EB%9F%AC-Router-Strategy%EB%A1%9C-%EC%BB%B4%ED%8F%AC%EB%84%8C%ED%8A%B8-%EC%9E%AC%EC%82%AC%EC%9A%A9%ED%95%98%EA%B8%B0
 */
export class CacheRouteReuseStrategy implements RouteReuseStrategy {

  /** 
   * 키로 인덱싱 된 RouteStorageObjects를 저장할 객체
   * 키는 모두 경로가됩니다 (route.routeConfig.path에서와 같이).
   * 이렇게하면 요청 된 경로에 대해 저장된 경로가 있는지 확인할 수 있습니다.
   */
  handlers: { [key: string]: DetachedRouteHandle } = {};

  /**
   * 경로 저장시기 결정
   * 경로를 저장해야하는 경우 부울이 컨트롤러에 this.store를 실행할지 여부를 표시한다고 생각합니다.
   * _When_ 호출은 특별히 중요하지 않지만 이것이 우리가 경로를 저장할지 여부를 결정한다는 것을 알아 두십시오
   * 여기서 수행 할 작업에 대한 아이디어 : route.routeConfig.path를 확인하여 저장하려는 경로인지 확인하십시오.
   * @param route 이것은 적어도 내가 아는 한 사용자가 현재있는 경로이며 저장하고 싶은지 알고 싶습니다.
   * @returns boolean은 해당 경로를 저장 (true)하거나 저장하지 않을 (false) 것을 나타냅니다.
   */
  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return !!route.data && !!(route.data as any).shouldDetach;
  }

  /**
   * 저장할`RouteStorageObject` 유형의 객체를 생성 한 다음 나중에 첨부 할 수 있도록 저장합니다.
   * @param route 이것은 나중에 요청 된 경로와 비교하기 위해 저장됩니다.`this.shouldAttach`를 참조하십시오.
   * @param handle 핸들 나중에 this.retrieve에 의해 검색되며이 클래스를 사용하는 모든 컨트롤러에 제공됩니다.
   */
  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
    this.handlers[route.routeConfig.path] = handle;
  }

  /**
   * 저장된 경로가 있는지 여부 및있는 경우 요청 된 경로 대신 렌더링해야하는지 여부를 결정합니다.
   * @param route 사용자가 요청한 경로
   * @return 저장된 경로를 렌더링할지 여부를 나타내는 부울을 반환합니다.
   */
  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    /**
     * 같은 Depth에서 하위 주소가 변경 되는 경우를 인지하여 처리하는 방법 모색중
     * 현재 임시방편으로 계정 < - > 공지 이동 간 페이지 전환 안되는 현상 수정
     */
    return !!route.routeConfig && !!this.handlers[route.routeConfig.path];
  }

  /**
   * 요청 된 경로의 로컬에 저장된 인스턴스 (있는 경우)를 찾아 반환합니다.
   * @param route 사용자가 요청한 새 경로
   * @returns 구성 요소를 렌더링하는 데 사용할 수있는 DetachedRouteHandle 개체
   */
  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle {
    if (!route.routeConfig) { return null; }
    return this.handlers[route.routeConfig.path];
  }

  /** 
   * 현재 경로의 재사용 여부 결정
   * @param future 라우터에 의해 트리거 된 사용자가가는 경로
   * @param curr 사용자가 현재있는 경로
   * @returns boolean 기본적으로 사용자가 현재 경로를 떠나려는 경우 true를 나타냅니다.ds to leave the current route
   */
  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    return future.routeConfig === curr.routeConfig;
  }

  /**
   * 핸들러 모두 초기화
   */
  clearHandlers() {
    for (const key in this.handlers) {
      this.deactivateOutlet(this.handlers[key]);
    }
    this.handlers = {};
  }

  /**
   * 전달받은 키정보에 해당하는 컴포넌트 핸들러만 초기화한다.
   * @param key
   * @returns 
   */
  clearHandler(key: string): void {
    if (!key) return;

    this.deactivateOutlet(this.handlers[key]);
    delete this.handlers[key];
  }

  private deactivateOutlet(handle: DetachedRouteHandle): void {

    if (handle && handle.hasOwnProperty['componentRef']) {
      const componentRef: ComponentRef<any> = handle['componentRef'];
      if (componentRef) {
        componentRef.destroy();
      }
    }
  }
}
